import { ChangeEvent, useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  FormLabel,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import { StyledSeperator } from "./Seperator";
import { StyledInput } from "./StyledInput";
import usePincode from "../../hooks/usePincode";
import { storePickupAddress, useAddPickupAddress } from "../../config/api";
import { usePickupAddress } from "../../hooks/usePickupAddress";
import { StyledButton } from "./StyledButton";

export interface PickupAddressFormProps {
  nickname: string;
  person_name: string;
  company_name: string;
  mobile_number: string;
  email: string;
  address: string;
  postal_code: string;
  city: string;
  state: string;
  [key: string]: string | number;
}

const PickupAddressDrawer = () => {
  const defaultPickupForm: PickupAddressFormProps = {
    nickname: "",
    person_name: "",
    company_name: "",
    mobile_number: "",
    email: "",
    address: "",
    postal_code: "",
    city: "",
    state: "",
  };

  const [pincode, setPincode] = useState("");

  const { isOpen, onClose } = usePickupAddress();

  const [formData, setFormData] =
    useState<PickupAddressFormProps>(defaultPickupForm);

  const handlePincodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = onlyNumber(e, 6);
    setPincode(e.target.value);
    handleInputChange(e, "postal_code");
  };

  const { city, state } = usePincode(pincode);

  const onlyNumber = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    limit: number
  ) => event.target.value.replace(/[^0-9]/g, "").slice(0, limit);

  // const handleNumberChange = (
  //   event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  //   field: keyof PickupAddressFormProps
  // ) => {
  //   event.target.value = onlyNumber(event, 10);

  //   handleInputChange(event, field);
  // };
const handleNumberChange = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  field: keyof PickupAddressFormProps
) => {
  let value = onlyNumber(event, 10);

  value = value.replace(/^0+/, "");

  event.target.value = value;

  handleInputChange(event, field);
};

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof PickupAddressFormProps
  ) => {
    const newFormData = { ...formData };
    newFormData[field] = e.target.value;
    setFormData(newFormData);
  };

  useEffect(() => {
    const newFormData = { ...formData };
    newFormData.city = city;
    newFormData.state = state;
    setFormData(newFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, state]);

  const { storeAddress } = useAddPickupAddress();

  const onSubmit = () => {
    storeAddress(formData, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const validateForm = useCallback(() => {
    return (
      formData.address.trim() !== "" &&
      formData.postal_code.trim() !== "" &&
      formData.company_name.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.mobile_number.trim() !== "" &&
      formData.person_name.trim() !== "" &&
      formData.state.trim() !== "" &&
      formData.city.trim() !== "" &&
      formData.nickname.trim() !== "" &&
      ifValidEmail(formData.email)
    );
  }, [formData]);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);

  useEffect(() => {
    const isValid = validateForm();

    setCanSubmit(isValid);
  }, [validateForm]);

  const [emailError, setEmailError] = useState<boolean>(false);

  const ifValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const checkEmailAtEveryStep = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const isValidEmail = ifValidEmail(e.target.value);
    const newFormData = { ...formData };
    newFormData.email = e.target.value;
    setFormData(newFormData);
    isValidEmail ? setEmailError(false) : setEmailError(true);
  };

  const closehandler = () => {
    setEmailError(false);
    setFormData(defaultPickupForm);
    onClose();
  };

  return (
    <>
      <Drawer open={isOpen} onClose={closehandler} anchor="right">
        <Box
          sx={{
            padding: "20px",
          }}
        >
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Typography>Add New Pick Up Address</Typography>
            <Button onClick={closehandler}>X</Button>
          </Stack>
          <StyledSeperator />

          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormLabel>
                  <Typography fontSize={12}>Nickname</Typography>
                </FormLabel>
                <StyledInput
                  value={formData.nickname}
                  onChange={(e) => handleInputChange(e, "nickname")}
                  placeholder="Nickname"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormLabel>
                  <Typography fontSize={12}>Contact Person</Typography>
                </FormLabel>
                <StyledInput
                  value={formData.person_name}
                  onChange={(e) => handleInputChange(e, "person_name")}
                  placeholder="Name of the person to be contacted"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormLabel>
                  <Typography fontSize={12}>Contact Number</Typography>
                </FormLabel>
                <StyledInput
                  value={formData.mobile_number}
                  onChange={(e) => handleNumberChange(e, "mobile_number")}
                  placeholder="Enter 10 digit mobile number"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormLabel>
                  <Typography fontSize={12}>Email Address</Typography>
                </FormLabel>
                <StyledInput
                  value={formData.email}
                  onChange={(e) => checkEmailAtEveryStep(e)}
                  placeholder="abcd@example.com"
                  fullWidth
                />
                {emailError && (
                  <Typography color={"red"}>
                    Please provide valid email
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormLabel>
                  <Typography fontSize={12}>Company Name</Typography>
                </FormLabel>
                <StyledInput
                  value={formData.company_name}
                  onChange={(e) => handleInputChange(e, "company_name")}
                  placeholder="Company Name"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <StyledSeperator />
              </Grid>

              <Grid item xs={12}>
                <Typography>
                  How can the delivery person reach the address?
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={8}>
                <FormLabel>
                  <Typography fontSize={12}>Complete Address</Typography>
                </FormLabel>
                <StyledInput
                  value={formData.address}
                  placeholder="House/Floor No. Building Name or Street, Locality"
                  fullWidth
                  onChange={(e) => handleInputChange(e, "address")}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormLabel>
                  <Typography fontSize={12}>Pincode</Typography>
                </FormLabel>
                <StyledInput
                  value={formData.postal_code}
                  onChange={handlePincodeChange}
                  placeholder="Enter Buyer's Pincode"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormLabel>
                  <Typography fontSize={12}>City</Typography>
                </FormLabel>
                <StyledInput
                  placeholder="Enter Buyer's City"
                  fullWidth
                  disabled
                  value={formData.city}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormLabel>
                  <Typography fontSize={12}>State</Typography>
                </FormLabel>
                <StyledInput
                  placeholder="Please Select State"
                  fullWidth
                  disabled
                  value={formData.state}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Stack
                  flexDirection={"row"}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                  marginTop={8}
                  gap={4}
                >
                  <StyledButton variant="outlined" onClick={closehandler}>
                    Cancel
                  </StyledButton>
                  <StyledButton
                    variant="contained"
                    disabled={!canSubmit}
                    onClick={onSubmit}
                  >
                    Verify and Save Address
                  </StyledButton>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
export default PickupAddressDrawer;
